import React, { useEffect, useMemo, useState } from 'react'
import { Box, Button, Typography } from '@mui/material'
import { CalendarDaysIcon, CalendarIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { useSearchParams } from 'react-router-dom'
import { FACILITATOR_CONTROL_TOWER_VIEW, FACILITATOR_VIEW_PARAM } from '../../../../../pure-js/libs/assistantNavigation'
import { useTranslation } from '../../../shared/hooks/useIntractionTranslate'
import { useFacilitatorLiveSessionMonitor, useUpcomingActivities } from '../../../apiLayer/queryHooks/queries'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../../constants/colors'
import { SessionListItem } from '../components/ManageSessions/SessionListItem'
import { ActivityWithSprint } from '../components/ManageSessions/helpers'
import { PageHeader } from '../../../shared/components/PageHeader'
import Spinner from '../../../interaxDesignSystemComponents/Spinner'
import { PageWrapper } from '../../../shared/components'
import { FacilitatorControlTower } from '../components/ControlTower/FacilitatorControlTower'

interface GroupedActivities {
  [date: string]: ActivityWithSprint[]
}

enum FacilitatorUpcomingView {
  ACTIVITIES = 'activities',
  CONTROL_TOWER = 'controlTower'
}

/**
 * Upcoming Activities Facilitator Page - Shows upcoming and active activities grouped by date
 */
export const UpcomingActivitiesFacilitatorPage: React.FC = () => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  // The assistant can deep-link straight to the control tower, and it may do so while this page is
  // already mounted, so the query string is honoured on mount and on every later change.
  const controlTowerRequested = searchParams.get(FACILITATOR_VIEW_PARAM) === FACILITATOR_CONTROL_TOWER_VIEW
  const [viewMode, setViewMode] = useState<FacilitatorUpcomingView>(
    controlTowerRequested ? FacilitatorUpcomingView.CONTROL_TOWER : FacilitatorUpcomingView.ACTIVITIES
  )

  useEffect(() => {
    if (controlTowerRequested) setViewMode(FacilitatorUpcomingView.CONTROL_TOWER)
  }, [controlTowerRequested])

  // Fetch upcoming activities using proper query hook
  const { data: activitiesResponse, isLoading, error } = useUpcomingActivities()
  const {
    data: liveMonitorResponse,
    isLoading: isLiveMonitorLoading,
    error: liveMonitorError
  } = useFacilitatorLiveSessionMonitor()

  // Filter and group activities by date
  const groupedActivities = useMemo<GroupedActivities>(() => {
    const activities = ((activitiesResponse?.data ?? []) as ActivityWithSprint[]).slice()
    const now = new Date()

    // Filter for upcoming activities only
    const upcomingActivities = activities.filter((activity) => {
      const activityDate = new Date(activity.startDate)
      return activityDate >= now || !activity.endedAt
    })

    // Sort by start date
    upcomingActivities.sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())

    // Group by date
    const grouped: GroupedActivities = {}
    upcomingActivities.forEach((activity) => {
      const dateKey = new Date(activity.startDate).toISOString().split('T')[0]
      if (!grouped[dateKey]) {
        grouped[dateKey] = []
      }
      grouped[dateKey].push(activity)
    })

    return grouped
  }, [activitiesResponse?.data])

  // TODO: Uncomment these stats when the Stats Cards section is re-enabled
  // const totalUpcoming = useMemo(() => {
  //   return Object.values(groupedActivities).reduce((sum, activities) => sum + activities.length, 0)
  // }, [groupedActivities])

  // const totalParticipants = useMemo(() => {
  //   const participantSet = new Set<string>()
  //   activities.forEach((activity) => {
  //     activity.participants?.forEach((p: any) => {
  //       if (p.email) participantSet.add(p.email)
  //     })
  //   })
  //   return participantSet.size
  // }, [activities])

  // const activeSprints = useMemo(() => {
  //   const sprintSet = new Set<string>()
  //   activities.forEach((activity) => {
  //     if (activity.sprintId) sprintSet.add(activity.sprintId)
  //   })
  //   return sprintSet.size
  // }, [activities])

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px'
        }}
      >
        <Spinner size="large" color="sun" text={t('facilitator.sessions.loading')} />
      </Box>
    )
  }

  // Check for API response errors (when handler returns success: false)
  const hasApiError = activitiesResponse?.success === false || activitiesResponse?.error

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px'
        }}
      >
        <Typography sx={{ color: INTERAX_COLORS.white }}>
          {t('facilitator.sessions.error')}: {error.message}
        </Typography>
      </Box>
    )
  }

  return (
    <PageWrapper>
      <PageHeader
        badgeText="ACTIVITIES"
        badgeIcon={<CalendarDaysIcon />}
        title={t('facilitator.sessions.title')}
        subtitle={t('facilitator.sessions.subtitle')}
        actions={
          <Box
            data-testid="activities-view-toggle"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              p: 0.5,
              borderRadius: '999px',
              backgroundColor: INTERAX_COLORS.white04,
              border: `1px solid ${INTERAX_COLORS.white10}`
            }}
          >
            <Button
              type="button"
              size="small"
              data-testid="activities-view-all"
              aria-pressed={viewMode === FacilitatorUpcomingView.ACTIVITIES}
              onClick={() => setViewMode(FacilitatorUpcomingView.ACTIVITIES)}
              sx={{
                minWidth: 0,
                px: 1.75,
                py: 0.75,
                borderRadius: '999px',
                textTransform: 'none',
                fontWeight: 600,
                color:
                  viewMode === FacilitatorUpcomingView.ACTIVITIES ? INTERAX_COLORS.offBlack : INTERAX_COLORS.white70,
                backgroundColor: viewMode === FacilitatorUpcomingView.ACTIVITIES ? INTERAX_COLORS.sun : 'transparent'
              }}
            >
              {t('facilitator.sessions.viewAllActivities', 'All activities')}
            </Button>
            <Button
              type="button"
              size="small"
              data-testid="activities-view-control-tower"
              aria-pressed={viewMode === FacilitatorUpcomingView.CONTROL_TOWER}
              onClick={() => setViewMode(FacilitatorUpcomingView.CONTROL_TOWER)}
              sx={{
                minWidth: 0,
                px: 1.75,
                py: 0.75,
                borderRadius: '999px',
                textTransform: 'none',
                fontWeight: 600,
                color:
                  viewMode === FacilitatorUpcomingView.CONTROL_TOWER ? INTERAX_COLORS.offBlack : INTERAX_COLORS.white70,
                backgroundColor: viewMode === FacilitatorUpcomingView.CONTROL_TOWER ? INTERAX_COLORS.sun : 'transparent'
              }}
            >
              {t('facilitator.sessions.viewControlTower', 'Control Tower')}
            </Button>
          </Box>
        }
      />

      {viewMode === FacilitatorUpcomingView.CONTROL_TOWER ? (
        <FacilitatorControlTower
          data={liveMonitorResponse?.data}
          isLoading={isLiveMonitorLoading}
          error={
            liveMonitorError instanceof Error
              ? liveMonitorError.message
              : liveMonitorResponse?.success === false
                ? liveMonitorResponse.error || liveMonitorResponse.data?.error || 'Failed to load live monitor'
                : null
          }
        />
      ) : hasApiError ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '200px',
            backgroundColor: INTERAX_COLORS.heritageBlue,
            borderRadius: '12px',
            border: `1px solid ${INTERAX_COLORS.white10}`,
            padding: '0px',
            gap: 2
          }}
        >
          {/* Error Icon */}
          <Box
            sx={{
              width: '64px',
              height: '64px',
              borderRadius: '12px',
              backgroundColor: INTERAX_COLORS.redErrorBackground,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <ExclamationTriangleIcon
              style={{
                width: '32px',
                height: '32px',
                color: INTERAX_COLORS.redError
              }}
            />
          </Box>

          {/* Error Title */}
          <Typography
            sx={{
              fontFamily: 'Lexend Deca',
              fontStyle: 'normal',
              fontWeight: 600,
              fontSize: '18px',
              lineHeight: '28px',
              letterSpacing: '-0.5px',
              color: INTERAX_COLORS.white,
              display: 'flex',
              alignItems: 'center',
              textAlign: 'center',
              flex: 'none',
              order: 0,
              flexGrow: 0
            }}
          >
            we have currently a problem please try again later
          </Typography>
        </Box>
      ) : Object.keys(groupedActivities).length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '200px',
            backgroundColor: INTERAX_COLORS.heritageBlue,
            borderRadius: '12px',
            border: `1px solid ${INTERAX_COLORS.white10}`,
            padding: '0px',
            gap: 2
          }}
        >
          {/* Calendar Icon */}
          <Box
            sx={{
              width: '64px',
              height: '64px',
              borderRadius: '12px',
              backgroundColor: INTERAX_COLORS.sun40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <CalendarIcon
              style={{
                width: '32px',
                height: '32px',
                color: INTERAX_COLORS.sun
              }}
            />
          </Box>

          {/* Text */}
          <Typography
            sx={{
              fontFamily: 'Lexend Deca',
              fontStyle: 'normal',
              fontWeight: 600,
              fontSize: '18px',
              lineHeight: '28px',
              letterSpacing: '-0.5px',
              color: INTERAX_COLORS.white,
              display: 'flex',
              alignItems: 'center',
              width: '199px',
              height: '28px',
              flex: 'none',
              order: 0,
              flexGrow: 0
            }}
          >
            {t('facilitator.sessions.noScheduledActivities')}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {Object.entries(groupedActivities).map(([date, dateActivities]) => {
            const activityDate = new Date(date)
            const today = new Date()
            today.setHours(0, 0, 0, 0)
            const isToday = activityDate.toDateString() === today.toDateString()

            return (
              <Box key={date}>
                {/* Date Header */}
                <Typography
                  sx={{
                    ...INTERAX_TYPOGRAPHY.headline4,
                    fontSize: '24px',
                    lineHeight: '32px',
                    fontWeight: '700',
                    color: isToday ? INTERAX_COLORS.sun : INTERAX_COLORS.white,
                    display: 'flex',
                    alignItems: 'center',
                    mb: 2
                  }}
                >
                  {activityDate.toLocaleDateString('en-US', {
                    weekday: 'long',
                    day: 'numeric'
                  })}
                </Typography>

                {/* Activities for this date */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {dateActivities.map((activity) => (
                    <SessionListItem key={activity.id} activity={activity} />
                  ))}
                </Box>
              </Box>
            )
          })}
        </Box>
      )}
    </PageWrapper>
  )
}
